import { useCallback, useEffect, useState } from "react";

export type Density = "comfortable" | "compact";

const STORAGE_KEY = "workloop:density";

export function readDensity(): Density {
  if (typeof window === "undefined") return "comfortable";
  try {
    return localStorage.getItem(STORAGE_KEY) === "compact"
      ? "compact"
      : "comfortable";
  } catch {
    /* storage blocked (e.g. iframe/private mode SecurityError) — use default */
    return "comfortable";
  }
}

export function writeDensity(density: Density) {
  if (typeof document === "undefined") return;
  if (density === "compact") {
    document.documentElement.setAttribute("data-density", "compact");
  } else {
    document.documentElement.removeAttribute("data-density");
  }
  try {
    localStorage.setItem(STORAGE_KEY, density);
  } catch {
    /* localStorage not available — ignore */
  }
}

export function toggleDensityGlobal(): Density {
  const next = readDensity() === "comfortable" ? "compact" : "comfortable";
  writeDensity(next);
  return next;
}

let shortcutInstalled = false;

/** Install the Cmd/Ctrl+Shift+D shortcut globally. Idempotent. */
export function installDensityShortcut() {
  if (shortcutInstalled || typeof window === "undefined") return;
  shortcutInstalled = true;
  window.addEventListener("keydown", (e) => {
    if (
      (e.metaKey || e.ctrlKey) &&
      e.shiftKey &&
      e.key.toLowerCase() === "d"
    ) {
      e.preventDefault();
      toggleDensityGlobal();
    }
  });
}

/**
 * Reactive density hook. Keeps local state in sync with the
 * DOM attribute via MutationObserver so external changes
 * (keyboard shortcut, manual devtools edit) propagate.
 */
export function useDensity() {
  const [density, setDensityState] = useState<Density>(readDensity);

  useEffect(() => {
    writeDensity(density);
  }, [density]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "data-density") {
          setDensityState(readDensity());
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
  }, []);

  const toggleDensity = useCallback(() => {
    setDensityState((d) => (d === "comfortable" ? "compact" : "comfortable"));
  }, []);

  return { density, setDensity, toggleDensity };
}
